import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
    fetchParasutSettings,
    upsertParasutSettings,
    fetchParasutConnectUrl,
    fetchParasutAccounts,
    fetchParasutSyncQueue,
    retryParasutSyncItem,
} from "../../../api/requests/parasut-requests";

export const useParasutSettings = (companyId, onSaved) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [connecting, setConnecting] = useState(false);

    const [settings, setSettings] = useState({
        id: null,
        connected: false,
        enabled: true,
        eInvoiceEnabled: false,
        defaultCashAccountId: "",
        defaultBankAccountId: "",
    });

    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);

    const [queue, setQueue] = useState({ content: [], totalElements: 0, totalPages: 0, page: 0, size: 10 });
    const [queueLoading, setQueueLoading] = useState(false);
    const [queueStatus, setQueueStatus] = useState("");
    const [retryingId, setRetryingId] = useState(null);

    const loadSettings = useCallback(async () => {
        if (!companyId) return null;
        try {
            const res = await fetchParasutSettings(companyId);
            if (res?.data) {
                setSettings({
                    id: res.data.id ?? null,
                    connected: res.data.connected ?? false,
                    enabled: res.data.enabled ?? true,
                    eInvoiceEnabled: res.data.eInvoiceEnabled ?? false,
                    defaultCashAccountId: res.data.defaultCashAccountId ?? "",
                    defaultBankAccountId: res.data.defaultBankAccountId ?? "",
                });
                return res.data;
            }
            setSettings((s) => ({ ...s, connected: false, id: null }));
            return null;
        } catch (e) {
            console.error("Paraşüt ayarları yüklenemedi", e);
            return null;
        }
    }, [companyId]);

    const loadAccounts = useCallback(async () => {
        if (!companyId) return;
        setAccountsLoading(true);
        try {
            const res = await fetchParasutAccounts(companyId);
            setAccounts(Array.isArray(res?.data) ? res.data : []);
        } catch (e) {
            console.error("Paraşüt hesapları yüklenemedi", e);
        } finally {
            setAccountsLoading(false);
        }
    }, [companyId]);

    const loadQueue = useCallback(async (page = 0, status = queueStatus, size = queue.size) => {
        if (!companyId) return;
        setQueueLoading(true);
        try {
            const res = await fetchParasutSyncQueue(companyId, { status, page, size });
            if (res?.data) {
                setQueue({
                    content: Array.isArray(res.data.content) ? res.data.content : [],
                    totalElements: res.data.totalElements ?? 0,
                    totalPages: res.data.totalPages ?? 0,
                    page: res.data.page ?? page,
                    size: res.data.size ?? size,
                });
            }
        } catch (e) {
            console.error("Paraşüt kuyruğu yüklenemedi", e);
        } finally {
            setQueueLoading(false);
        }
    }, [companyId, queueStatus, queue.size]);

    // İlk yükleme + OAuth dönüşü (?parasut=connected / error) yakalama
    useEffect(() => {
        if (!companyId) return;
        const params = new URLSearchParams(window.location.search);
        const result = params.get("parasut");
        if (result) {
            if (result === "connected") toast.success("Paraşüt hesabınız bağlandı");
            else toast.error("Paraşüt bağlantısı başarısız oldu");
            params.delete("parasut");
            params.delete("companyId");
            const clean = window.location.pathname + (params.toString() ? `?${params}` : "");
            window.history.replaceState({}, "", clean);
        }
        (async () => {
            setLoading(true);
            const data = await loadSettings();
            setLoading(false);
            if (data?.connected) {
                loadAccounts();
                loadQueue(0);
            }
        })();
    }, [companyId, loadSettings, loadAccounts, loadQueue]);

    const connect = async () => {
        setConnecting(true);
        try {
            const res = await fetchParasutConnectUrl(companyId);
            if (res?.data) {
                window.location.href = res.data; // Paraşüt yetkilendirme sayfasına yönlendir
            } else {
                toast.error(res?.message || "Bağlantı bağlantısı alınamadı");
                setConnecting(false);
            }
        } catch (e) {
            toast.error("Paraşüt bağlantısı başlatılamadı");
            setConnecting(false);
        }
    };

    const save = async (overrides = {}) => {
        setSaving(true);
        try {
            const payload = {
                companyId,
                enabled: settings.enabled,
                eInvoiceEnabled: settings.eInvoiceEnabled,
                defaultCashAccountId: settings.defaultCashAccountId || null,
                defaultBankAccountId: settings.defaultBankAccountId || null,
                ...overrides,
            };
            const res = await upsertParasutSettings(payload);
            if (res?.data) {
                toast.success("Paraşüt ayarları kaydedildi");
                await loadSettings();
                onSaved?.();
            } else {
                toast.error(res?.message || "Paraşüt ayarları kaydedilemedi");
            }
        } catch (e) {
            toast.error("Paraşüt ayarları kaydedilirken hata oluştu");
        } finally {
            setSaving(false);
        }
    };

    const changeQueueStatus = (status) => {
        setQueueStatus(status);
        loadQueue(0, status);
    };

    const retryItem = async (queueId) => {
        setRetryingId(queueId);
        try {
            const res = await retryParasutSyncItem(companyId, queueId);
            if (res?.data === true) {
                toast.success("Kayıt yeniden denemeye alındı");
                loadQueue(queue.page);
            } else {
                toast.error(res?.message || "Yeniden deneme başarısız");
            }
        } catch (e) {
            toast.error("Yeniden deneme sırasında hata oluştu");
        } finally {
            setRetryingId(null);
        }
    };

    return {
        loading, saving, connecting,
        settings, setSettings,
        accounts, accountsLoading,
        connect, save,
        queue, queueLoading, queueStatus, changeQueueStatus, loadQueue, retryingId, retryItem,
    };
};
